import { buildFormulationRows } from './investigationFixtures';

// Placeholder data for the Formulary Analyze page.
//
// TEMPORARY — every export mirrors the response shape of an endpoint that does
// not exist yet. The seam is services/analysis.js. Delete this file once the
// endpoints are live.
//
// WHERE THE NUMBERS COME FROM
// The current-state rows are NOT generated here: they come from
// investigationFixtures.buildFormulationRows, the same source Investigate reads.
// Analyze is a what-if layered over the formulations Investigate lists, so a
// second generator would let the two pages disagree about the same medication.
//
// The counterfactual is then real arithmetic over those rows, reproducing the
// demo backend's SQL rather than hashing a plausible-looking answer:
//
//   new_containers = GREATEST(CEIL(units_infused / (new_conc * new_volume)),
//                             expirations)
//   waste%         = (container_units * containers - units_infused)
//                    / (container_units * containers) * 100
//   new_rate       = old_rate * old_conc / new_conc
//
// That matters because the whole page is a comparison: if the updated column
// were hashed independently of the current one, moving a row to a richer
// formulation could show MORE waste, and the page would be teaching the user
// something false. Deriving it keeps the direction of every metric honest, and
// makes this file a specification the eventual LookML has to match.

// Units a container holds = concentration x volume. The one quantity the whole
// counterfactual turns on.
const containerUnits = (concentration, volume) => concentration * volume;

// Per-session container expirations, as a floor under the recomputed count: a
// container that timed out was dispensed no matter how little came out of it.
// Roughly 4% of current containers, so it only binds on large concentration
// increases — which is exactly where it should.
const expirationFloor = (containers) => Math.floor(containers * 0.04);

// Total drug actually infused across the period. Derived from the current
// formulation and its observed waste, so it stays fixed under the counterfactual
// — it is the one quantity a formulation change does NOT alter, and every
// updated figure is computed against it.
function unitsInfused(row) {
  const dispensed = containerUnits(row.concentrationValue, row.volumeValue) * row.infusions;
  const wasteFraction = (row.wastePercentage ?? 0) / 100;
  return dispensed * (1 - wasteFraction);
}

// CEIL, floored by expirations: partial containers still get opened, and a
// container that expired was used regardless.
//
// An unchanged formulation returns the observed count rather than recomputing.
// The recomputation runs off `unitsInfused`, which is itself derived from the
// observed waste, so re-deriving it here would land on a different number and
// report a change where the user made none. The SQL guards this the same way.
function newContainers(row, updated) {
  const perContainer = containerUnits(updated.concentration, updated.volume);
  if (!perContainer) return null;

  if (updated.concentration === row.concentrationValue && updated.volume === row.volumeValue) {
    return row.infusions;
  }

  return Math.max(Math.ceil(unitsInfused(row) / perContainer), expirationFloor(row.infusions));
}

// Waste as the share of dispensed drug that never reached a patient.
function wastePercent(perContainer, containers, infused) {
  const dispensed = perContainer * containers;
  if (!dispensed) return null;
  return Math.round(((dispensed - infused) / dispensed) * 100);
}

// Flow rates scale inversely with concentration: the same dose per hour out of a
// stronger bag needs proportionally less volume per hour.
const scaleRate = (rate, currentConcentration, updatedConcentration) => {
  if (rate == null || !updatedConcentration) return null;
  if (currentConcentration === updatedConcentration) return Math.round(rate * 100) / 100;
  return Math.round(((rate * currentConcentration) / updatedConcentration) * 100) / 100;
};

// Containers per patient per day. The fixture has no session-level data, so this
// uses a fixed 3.5-day average stay — enough for the column to move with the
// container count, which is what it is there to show.
const AVERAGE_DAYS_ON_THERAPY = 3.5;
const bagsPerDay = (containers) => Math.round((containers / AVERAGE_DAYS_ON_THERAPY) * 10) / 10;

/**
 * ### 1 — GET /formulations (Analyze's current-state table).
 *
 * The same rows Investigate shows, plus the adult/pediatric split the drug score
 * needs. Investigate has no use for that split, so it isn't in its own contract;
 * the real endpoint returns it for both.
 */
export function buildAnalysisFormulations(scope = {}) {
  return buildFormulationRows(scope).map((row) => {
    // Pediatric share tracks concentration: dilute formulations skew pediatric,
    // concentrated ones adult. A fixed split would make every medication score
    // identically on the two concentration factors.
    const pediatricShare = row.concentrationValue <= 2 ? 0.45 : 0.08;
    const pediatricInfusions = Math.round(row.infusions * pediatricShare);

    return {
      ...row,
      wastePercentage: row.wasteWeightedPct,
      adultInfusions: row.infusions - pediatricInfusions,
      pediatricInfusions,
    };
  });
}

// The current-state row for one formulation string. Goes through
// buildAnalysisFormulations, not buildFormulationRows, so `wastePercentage` is
// populated — reading the raw Investigate row here treats every formulation as
// zero-waste and collapses the whole waste comparison to 0% -> 0%.
function currentRow(scope, formulation) {
  return buildAnalysisFormulations(scope).find((row) => row.formulation === formulation) || null;
}

/**
 * ### 2 — POST /analysis/formulary (per-row section metrics).
 *
 * `changes` is [{ currentFormulation, updatedFormulation, updated: {concentration, volume} }]
 * in ROW ORDER, and the response is positional so the caller can zip it back.
 *
 * A change whose current formulation isn't in scope yields null metrics for that
 * row alone — the same per-row degradation the real endpoint applies, so one bad
 * formulation can't drop every other row's analysis.
 */
export function buildFormularyAnalysis(scope = {}, changes = []) {
  return changes.map((change) => {
    const row = currentRow(scope, change.currentFormulation);
    if (!row || !change.updated) return {};

    const infused = unitsInfused(row);
    const currentPerContainer = containerUnits(row.concentrationValue, row.volumeValue);
    const updatedPerContainer = containerUnits(change.updated.concentration, change.updated.volume);
    const updatedContainers = newContainers(row, change.updated);

    if (updatedContainers == null) return {};

    return {
      currentWaste: wastePercent(currentPerContainer, row.infusions, infused),
      updatedWaste: wastePercent(updatedPerContainer, updatedContainers, infused),
      currentBagsPerDay: bagsPerDay(row.infusions),
      updatedBagsPerDay: bagsPerDay(updatedContainers),
      currentMinRate: scaleRate(row.minRate, 1, 1),
      updatedMinRate: scaleRate(row.minRate, row.concentrationValue, change.updated.concentration),
      currentMaxRate: scaleRate(row.maxRate, 1, 1),
      updatedMaxRate: scaleRate(row.maxRate, row.concentrationValue, change.updated.concentration),
      currentInfusions: row.infusions,
      updatedInfusions: updatedContainers,
    };
  });
}

/**
 * ### 3 — POST /analysis/summary (the cumulative impact tiles).
 *
 * Aggregates across every resolvable change, in one pass over the same
 * arithmetic. Returns the tile shape the store normalizes, including the
 * net-patient-impact rule: the dominant direction drives the count and the
 * percentage, and equal counts mean no net impact.
 */
export function buildAnalysisSummary(scope = {}, changes = []) {
  let currentDispensed = 0;
  let updatedDispensed = 0;
  let currentInfusions = 0;
  let updatedInfusions = 0;
  let infusedTotal = 0;
  let patientsMore = 0;
  let patientsFewer = 0;
  let resolved = 0;

  changes.forEach((change) => {
    const row = currentRow(scope, change.currentFormulation);
    if (!row || !change.updated) return;

    const updatedContainers = newContainers(row, change.updated);
    if (updatedContainers == null) return;

    resolved += 1;
    infusedTotal += unitsInfused(row);
    currentDispensed += containerUnits(row.concentrationValue, row.volumeValue) * row.infusions;
    updatedDispensed += containerUnits(change.updated.concentration, change.updated.volume) * updatedContainers;
    currentInfusions += row.infusions;
    updatedInfusions += updatedContainers;

    // One formulation stands in for its patients: the fixture has no session
    // rows, so the direction of its container change is the direction for all
    // of them.
    if (updatedContainers > row.infusions) patientsMore += 1;
    else if (updatedContainers < row.infusions) patientsFewer += 1;
  });

  if (resolved === 0) return null;

  const currentWaste = wastePercent(1, currentDispensed, infusedTotal) ?? 0;
  const updatedWaste = wastePercent(1, updatedDispensed, infusedTotal) ?? 0;
  const wasteDelta = updatedWaste - currentWaste;
  const unitsDelta = Math.round(updatedDispensed - currentDispensed);
  const infusionsDelta = updatedInfusions - currentInfusions;

  const moreDominant = patientsMore > patientsFewer;
  let patientCount = 0;
  if (patientsMore !== patientsFewer) {
    patientCount = moreDominant ? patientsMore : patientsFewer;
  }
  const dominantPct = resolved ? Math.round((patientCount / resolved) * 100) : 0;

  return {
    waste: {
      intValue: Math.abs(wasteDelta),
      isIncrease: wasteDelta > 0,
      text: wasteDelta > 0 ? 'increase in waste' : 'decrease in waste',
    },
    unitsDispensed: {
      intValue: Math.abs(unitsDelta),
      isIncrease: unitsDelta > 0,
      text: unitsDelta > 0 ? 'increase in units dispensed' : 'decrease in units dispensed',
    },
    infusionsDispensed: {
      intValue: Math.abs(infusionsDelta),
      isIncrease: infusionsDelta > 0,
      text: infusionsDelta > 0 ? 'increase in infusions' : 'decrease in infusions',
    },
    pctPatientsWithMoreInfusions: {
      intValue: patientCount,
      isIncrease: moreDominant,
      text: `(${dominantPct}%)`,
    },
    // Cost is always computed client-side from user-entered $/infusion; the real
    // endpoint returns $0 here too.
    cost: { stringValue: '$0', isIncrease: false, text: 'Medication Cost reduction' },
  };
}

export default {
  buildAnalysisFormulations,
  buildFormularyAnalysis,
  buildAnalysisSummary,
};
